'use client';

// Sharpen (unsharp mask) and unblur (Richardson-Lucy deconvolution) in one workspace, used by
// /tools/sharpen-image, /tools/image-sharpener and /tools/unblur-image. Processing runs at full
// resolution in image-sharpen.worker.ts; the preview is a before/after split that can zoom to
// 100%, because sharpening is invisible in a scaled-down thumbnail (2026-10-06 support ticket).

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';
import { formatFileSize, getBaseName, triggerDownload } from '@/lib/browser-image-tools';
import { detailScore, type SharpenSettings, type UnblurSettings } from '@/lib/image-sharpen-engine';
import type { SharpenWorkerRequest, SharpenWorkerResponse } from './image-sharpen.worker';
import type { AiEnhanceRequest, AiEnhanceResponse } from './image-ai-enhance.worker';

type Mode = 'sharpen' | 'unblur' | 'upscale';
type Method = 'deconv' | 'ai';
type AiState = {
  phase: 'idle' | 'download' | 'run' | 'done';
  fraction: number;
  device: string;
};
type Zoom = 'fit' | 1 | 2;
type OutputFormat = 'jpeg' | 'png' | 'webp';

const MAX_PIXELS = 24_000_000;
const FIT_MAX_HEIGHT = 620;
// Swin2SR enlarges 4x and is slow in a browser (~5k pixels/s on a GPU, ~650 on one CPU thread),
// so it runs on the image at its own resolution and only on images small enough to finish in a
// couple of minutes. Shrinking a big photo to fit, then scaling the result back up, destroyed
// detail instead of adding it (-73% on a sharp 1600 px photo, 2026-10-08): AI here is for
// enlarging small or low-resolution images, deconvolution is for sharpening big ones.
const AI_MAX_INPUT = { webgpu: 768, wasm: 320 };
const AI_PIXELS_PER_SECOND = { webgpu: 5000, wasm: 650 };

function resampleTo(source: CanvasImageSource, width: number, height: number) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true })!;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(source, 0, 0, width, height);
  return context.getImageData(0, 0, width, height);
}

const SHARPEN_PRESETS: Array<{ id: string; label: string; settings: SharpenSettings }> = [
  { id: 'light', label: 'Light', settings: { radius: 1, amount: 0.8, threshold: 2 } },
  { id: 'medium', label: 'Medium', settings: { radius: 1.6, amount: 1.4, threshold: 2 } },
  { id: 'strong', label: 'Strong', settings: { radius: 2.5, amount: 2.2, threshold: 3 } },
  { id: 'text', label: 'Text & screenshots', settings: { radius: 0.7, amount: 1.8, threshold: 0 } },
];

const UNBLUR_PRESETS: Array<{ id: string; label: string; settings: UnblurSettings }> = [
  { id: 'slight', label: 'Slightly soft', settings: { blur: 1.2, iterations: 10, noise: 2 } },
  { id: 'soft', label: 'Out of focus', settings: { blur: 2, iterations: 15, noise: 2 } },
  { id: 'heavy', label: 'Very blurry', settings: { blur: 3.2, iterations: 25, noise: 3 } },
];

const FORMATS: Record<
  OutputFormat,
  { label: string; mime: string; ext: string; quality?: number }
> = {
  jpeg: { label: 'JPG', mime: 'image/jpeg', ext: 'jpg', quality: 0.95 },
  png: { label: 'PNG', mime: 'image/png', ext: 'png' },
  webp: { label: 'WebP', mime: 'image/webp', ext: 'webp', quality: 0.95 },
};

function formatFor(file: File): OutputFormat {
  if (/jpe?g/i.test(file.type) || /\.jpe?g$/i.test(file.name)) return 'jpeg';
  if (/webp/i.test(file.type)) return 'webp';
  return 'png';
}

type Loaded = {
  file: File;
  width: number;
  height: number;
  scaled: boolean;
  pixels: ImageData;
  detailBefore: number;
};

export default function BrowserSharpenStudio({
  config,
}: {
  config?: { variant?: string; heading?: string; toolSlug?: string };
}) {
  const mode: Mode =
    config?.variant === 'unblur' ? 'unblur' : config?.variant === 'upscale' ? 'upscale' : 'sharpen';
  const toolSlug =
    config?.toolSlug ??
    (mode === 'unblur'
      ? 'unblur-image'
      : mode === 'upscale'
        ? 'ai-image-upscaler'
        : 'sharpen-image');
  const presets = mode === 'unblur' ? UNBLUR_PRESETS : SHARPEN_PRESETS;
  const defaultPreset = mode === 'unblur' ? 'soft' : 'medium';

  const inputRef = useRef<HTMLInputElement | null>(null);
  const beforeRef = useRef<HTMLCanvasElement | null>(null);
  const afterRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef<HTMLDivElement | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const jobRef = useRef(0);
  const dragRef = useRef<{
    kind: 'split' | 'pan';
    x: number;
    y: number;
    cx: number;
    cy: number;
  } | null>(null);

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [presetId, setPresetId] = useState(defaultPreset);
  const [sharpen, setSharpen] = useState<SharpenSettings>(
    SHARPEN_PRESETS.find((p) => p.id === 'medium')!.settings,
  );
  const [unblur, setUnblur] = useState<UnblurSettings>(
    UNBLUR_PRESETS.find((p) => p.id === 'soft')!.settings,
  );
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const [detailAfter, setDetailAfter] = useState<number | null>(null);
  const [split, setSplit] = useState(50);
  const [zoom, setZoom] = useState<Zoom>('fit');
  const [center, setCenter] = useState({ x: 0.5, y: 0.5 });
  const [viewWidth, setViewWidth] = useState(0);
  const [format, setFormat] = useState<OutputFormat>('jpeg');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [method, setMethod] = useState<Method>('deconv');
  const [ai, setAi] = useState<AiState>({ phase: 'idle', fraction: 0, device: '' });
  const [aiStrength, setAiStrength] = useState(1);
  const aiWorkerRef = useRef<Worker | null>(null);
  const aiResultRef = useRef<ImageData | null>(null);
  const aiJobRef = useRef(0);
  const useAi = mode === 'upscale' || (mode === 'unblur' && method === 'ai');
  const [aiScale, setAiScale] = useState<2 | 4>(mode === 'upscale' ? 4 : 2);
  // Size the comparison and the download use: the original, or the enlarged AI output.
  const [view, setView] = useState<{ width: number; height: number; baseDetail: number } | null>(
    null,
  );
  const aiBaseRef = useRef<ImageData | null>(null);
  const [aiDevice, setAiDevice] = useState<'webgpu' | 'wasm'>('wasm');

  useEffect(() => {
    const gpu = (navigator as Navigator & { gpu?: { requestAdapter: () => Promise<unknown> } }).gpu;
    if (!gpu) return;
    gpu
      .requestAdapter()
      .then((adapter) => setAiDevice(adapter ? 'webgpu' : 'wasm'))
      .catch(() => undefined);
  }, []);

  useEffect(
    () => () => {
      workerRef.current?.terminate();
      aiWorkerRef.current?.terminate();
    },
    [],
  );

  useEffect(() => {
    const node = viewRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setViewWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, [loaded]);

  async function handleFile(file: File | null | undefined) {
    setError(null);
    if (!file) return;
    if (!file.type.startsWith('image/') && !/\.(jpe?g|png|webp|gif|bmp|avif)$/i.test(file.name)) {
      setError('Choose a JPG, PNG, WebP, GIF, BMP or AVIF image.');
      return;
    }
    try {
      const bitmap = await createImageBitmap(file);
      let { width, height } = bitmap;
      let scaled = false;
      if (width * height > MAX_PIXELS) {
        const ratio = Math.sqrt(MAX_PIXELS / (width * height));
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        scaled = true;
      }
      const canvas = beforeRef.current!;
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('Your browser could not create a canvas.');
      context.drawImage(bitmap, 0, 0, width, height);
      bitmap.close();
      const pixels = context.getImageData(0, 0, width, height);
      const after = afterRef.current!;
      after.width = width;
      after.height = height;
      after.getContext('2d')!.putImageData(pixels, 0, 0);
      setFormat(formatFor(file));
      aiResultRef.current = null;
      aiBaseRef.current = null;
      setView(null);
      aiJobRef.current += 1;
      setAi((state) => ({ ...state, phase: 'idle', fraction: 0 }));
      setReady(false);
      setDetailAfter(null);
      setZoom('fit');
      setCenter({ x: 0.5, y: 0.5 });
      setLoaded({
        file,
        width,
        height,
        scaled,
        pixels,
        detailBefore: detailScore(pixels.data, width, height),
      });
    } catch {
      setError('This image could not be opened. Try exporting it again as JPG or PNG.');
    }
  }

  const run = useCallback(() => {
    if (!loaded) return;
    // A new setting cancels the job in flight: the old result is useless.
    workerRef.current?.terminate();
    const worker = new Worker(new URL('./image-sharpen.worker.ts', import.meta.url), {
      type: 'module',
    });
    workerRef.current = worker;
    const id = ++jobRef.current;
    setBusy(true);
    setProgress(0);
    worker.onmessage = (event: MessageEvent<SharpenWorkerResponse>) => {
      const message = event.data;
      if (message.id !== jobRef.current) return;
      if (message.type === 'progress') {
        setProgress(message.fraction);
        return;
      }
      setBusy(false);
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
      if (message.type === 'error') {
        setError(message.message);
        return;
      }
      const output = new ImageData(
        new Uint8ClampedArray(message.data),
        loaded.width,
        loaded.height,
      );
      afterRef.current?.getContext('2d')?.putImageData(output, 0, 0);
      setDetailAfter(detailScore(output.data, loaded.width, loaded.height));
      setReady(true);
    };
    worker.onerror = () => {
      setBusy(false);
      setError(
        'Processing stopped. The image may be too large for this device; try a smaller one.',
      );
    };
    const base = {
      id,
      width: loaded.width,
      height: loaded.height,
      data: new Uint8ClampedArray(loaded.pixels.data),
    };
    const request: SharpenWorkerRequest =
      mode === 'unblur'
        ? { ...base, mode: 'unblur', settings: unblur }
        : { ...base, mode: 'sharpen', settings: sharpen };
    worker.postMessage(request, [request.data.buffer]);
  }, [loaded, mode, sharpen, unblur]);

  useEffect(() => {
    if (!loaded || useAi) return;
    const timer = window.setTimeout(run, 250);
    return () => window.clearTimeout(timer);
  }, [loaded, run, useAi]);

  // Paint the AI result blended with the original at the chosen strength.
  const showAi = useCallback(
    (strength: number) => {
      const result = aiResultRef.current;
      const base = aiBaseRef.current;
      if (!loaded || !result || !base) return;
      const mixed = new Uint8ClampedArray(base.data.length);
      for (let i = 0; i < base.data.length; i += 1) {
        mixed[i] = base.data[i] + (result.data[i] - base.data[i]) * strength;
      }
      const output = new ImageData(mixed, result.width, result.height);
      afterRef.current?.getContext('2d')?.putImageData(output, 0, 0);
      setDetailAfter(detailScore(output.data, result.width, result.height));
      setReady(true);
    },
    [loaded],
  );

  function restoreOriginal() {
    if (!loaded) return;
    for (const canvas of [beforeRef.current, afterRef.current]) {
      if (!canvas) continue;
      canvas.width = loaded.width;
      canvas.height = loaded.height;
      canvas.getContext('2d')?.putImageData(loaded.pixels, 0, 0);
    }
    setView(null);
  }

  function chooseMethod(next: Method) {
    setMethod(next);
    if (!loaded) return;
    if (next === 'deconv') {
      restoreOriginal();
      return;
    }
    if (next === 'ai') {
      workerRef.current?.terminate();
      workerRef.current = null;
      setBusy(false);
      restoreOriginal();
      aiResultRef.current = null;
      aiBaseRef.current = null;
      setAi((state) => ({ ...state, phase: 'idle', fraction: 0 }));
      setDetailAfter(null);
      setReady(false);
    }
  }

  async function enhanceWithAi() {
    if (!loaded || !beforeRef.current) return;
    setError(null);
    const gpu = (navigator as Navigator & { gpu?: { requestAdapter: () => Promise<unknown> } }).gpu;
    let webgpu = false;
    try {
      webgpu = Boolean(gpu && (await gpu.requestAdapter()));
    } catch {
      webgpu = false;
    }
    const cap = webgpu ? AI_MAX_INPUT.webgpu : AI_MAX_INPUT.wasm;
    if (Math.max(loaded.width, loaded.height) > cap) {
      setError(
        `AI upscaling works on images up to ${cap} px on this device; this one is ${Math.max(loaded.width, loaded.height)} px. Larger photos already have the pixels; to make them crisper use ${mode === 'unblur' ? 'Deconvolution' : 'Unblur Image or Sharpen Image'}.`,
      );
      return;
    }
    restoreOriginal();
    const work = { width: loaded.width, height: loaded.height };
    const input = loaded.pixels;
    const scale = aiScale;
    const start = (allowWebGpu: boolean) => {
      if (!aiWorkerRef.current) {
        aiWorkerRef.current = new Worker(new URL('./image-ai-enhance.worker.ts', import.meta.url), {
          type: 'module',
        });
      }
      const worker = aiWorkerRef.current;
      const id = ++aiJobRef.current;
      setAi({ phase: 'download', fraction: 0, device: '' });
      worker.onmessage = (event: MessageEvent<AiEnhanceResponse>) => {
        const message = event.data;
        if (message.id !== aiJobRef.current) return;
        if (message.type === 'status') {
          setAi({ phase: message.phase, fraction: message.fraction, device: message.device });
          return;
        }
        if (message.type === 'error') {
          worker.terminate();
          aiWorkerRef.current = null;
          // A failed WebGPU session leaves the runtime unusable: retry once in a fresh worker
          // on the CPU, at the smaller size the CPU can finish.
          if (
            message.device === 'webgpu' &&
            Math.max(loaded.width, loaded.height) <= AI_MAX_INPUT.wasm
          ) {
            start(false);
            return;
          }
          setAi((state) => ({ ...state, phase: 'idle' }));
          setError(message.message);
          return;
        }
        const upscaled = document.createElement('canvas');
        upscaled.width = message.width;
        upscaled.height = message.height;
        upscaled
          .getContext('2d')!
          .putImageData(
            new ImageData(new Uint8ClampedArray(message.data), message.width, message.height),
            0,
            0,
          );
        const outWidth = loaded.width * scale;
        const outHeight = loaded.height * scale;
        // 2x is the 4x result scaled down: sharper than asking for 2x directly.
        aiResultRef.current = resampleTo(upscaled, outWidth, outHeight);
        // The "before" side is the original enlarged the ordinary way, so the slider shows
        // what AI adds over a plain resize.
        const original = document.createElement('canvas');
        original.width = loaded.width;
        original.height = loaded.height;
        original.getContext('2d')!.putImageData(loaded.pixels, 0, 0);
        const base = resampleTo(original, outWidth, outHeight);
        aiBaseRef.current = base;
        for (const canvas of [beforeRef.current, afterRef.current]) {
          if (!canvas) continue;
          canvas.width = outWidth;
          canvas.height = outHeight;
        }
        beforeRef.current?.getContext('2d')?.putImageData(base, 0, 0);
        setView({
          width: outWidth,
          height: outHeight,
          baseDetail: detailScore(base.data, outWidth, outHeight),
        });
        setAi((state) => ({ ...state, phase: 'done', fraction: 1 }));
        showAi(aiStrength);
      };
      worker.onerror = () => {
        setAi((state) => ({ ...state, phase: 'idle' }));
        setError(
          'AI upscaling stopped. This device may not have enough memory; try a smaller image.',
        );
        aiWorkerRef.current?.terminate();
        aiWorkerRef.current = null;
      };
      request.id = id;
      request.allowWebGpu = allowWebGpu;
      const payload = { ...request, data: new Uint8ClampedArray(request.data) };
      worker.postMessage(payload, [payload.data.buffer]);
    };
    const request: AiEnhanceRequest = {
      id: 0,
      width: work.width,
      height: work.height,
      data: new Uint8ClampedArray(input.data),
      allowWebGpu: webgpu,
    };
    start(webgpu);
  }

  const aiRunning = ai.phase === 'download' || ai.phase === 'run';

  function applyPreset(id: string) {
    setPresetId(id);
    if (mode === 'unblur') {
      const preset = UNBLUR_PRESETS.find((p) => p.id === id);
      if (preset) setUnblur(preset.settings);
    } else {
      const preset = SHARPEN_PRESETS.find((p) => p.id === id);
      if (preset) setSharpen(preset.settings);
    }
  }

  async function download() {
    if (!loaded || !afterRef.current || !ready) return;
    setSaving(true);
    setError(null);
    try {
      // Usage is counted in the background; a failed API call must never block the download.
      void consumeToolUsage(toolSlug, '').catch(() => undefined);
      const option = FORMATS[format];
      const blob = await new Promise<Blob | null>((resolve) =>
        afterRef.current!.toBlob(resolve, option.mime, option.quality),
      );
      if (!blob) throw new Error('The image could not be encoded.');
      const url = URL.createObjectURL(blob);
      const suffix = view ? `upscaled-${aiScale}x` : mode === 'unblur' ? 'unblurred' : 'sharpened';
      triggerDownload(url, `${getBaseName(loaded.file.name)}-${suffix}.${option.ext}`);
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The download failed.');
    } finally {
      setSaving(false);
    }
  }

  // Geometry of the comparison view. "Fit" shows the whole photo, capped in height so the
  // controls stay in reach; at 100%/200% one image pixel is 1 or 2 CSS pixels and the view pans
  // around `center`.
  const dims = view ?? loaded;
  const fitScale = dims ? Math.min(viewWidth / dims.width, FIT_MAX_HEIGHT / dims.height) : 0;
  const scale = dims ? (zoom === 'fit' ? fitScale || 0 : zoom) : 0;
  const drawnWidth = dims ? dims.width * scale : 0;
  const drawnHeight = dims ? dims.height * scale : 0;
  const viewHeight = loaded
    ? zoom === 'fit'
      ? Math.round(drawnHeight)
      : Math.min(FIT_MAX_HEIGHT, Math.max(Math.round(dims!.height * fitScale), 320))
    : 0;
  const clampOffset = (value: number, drawn: number, view: number) =>
    drawn <= view ? (view - drawn) / 2 : Math.min(0, Math.max(view - drawn, value));
  const offsetX = clampOffset(viewWidth / 2 - center.x * drawnWidth, drawnWidth, viewWidth);
  const offsetY = clampOffset(viewHeight / 2 - center.y * drawnHeight, drawnHeight, viewHeight);
  const layerStyle = {
    width: drawnWidth,
    height: drawnHeight,
    transform: `translate(${offsetX}px, ${offsetY}px)`,
  };

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (!loaded) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const dividerX = (rect.width * split) / 100;
    // Grabbing the divider (or its knob) moves it; anywhere else zooms in or pans.
    if (Math.abs(event.clientX - rect.left - dividerX) < 22) {
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = { kind: 'split', x: 0, y: 0, cx: 0, cy: 0 };
      return;
    }
    if (zoom === 'fit') {
      setCenter({
        x: (event.clientX - rect.left - offsetX) / drawnWidth,
        y: (event.clientY - rect.top - offsetY) / drawnHeight,
      });
      setZoom(1);
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      kind: 'pan',
      x: event.clientX,
      y: event.clientY,
      cx: center.x,
      cy: center.y,
    };
  }

  function onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.kind === 'split') {
      const rect = event.currentTarget.getBoundingClientRect();
      setSplit(Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100)));
      return;
    }
    if (!drawnWidth) return;
    setCenter({
      x: Math.min(1, Math.max(0, drag.cx - (event.clientX - drag.x) / drawnWidth)),
      y: Math.min(1, Math.max(0, drag.cy - (event.clientY - drag.y) / drawnHeight)),
    });
  }

  const detailBase = view ? view.baseDetail : (loaded?.detailBefore ?? 0);
  const gain =
    loaded && detailAfter !== null && detailBase > 0
      ? Math.round((detailAfter / detailBase - 1) * 100)
      : null;
  const otherTool =
    mode === 'unblur'
      ? { href: '/tools/sharpen-image', label: 'Sharpen Image' }
      : mode === 'upscale'
        ? { href: '/tools/unblur-image', label: 'Unblur Image' }
        : { href: '/tools/unblur-image', label: 'Unblur Image' };
  const aiCap = AI_MAX_INPUT[aiDevice];
  const aiTooBig = Boolean(loaded && Math.max(loaded.width, loaded.height) > aiCap);
  const aiSeconds = loaded
    ? Math.max(15, Math.round((loaded.width * loaded.height) / AI_PIXELS_PER_SECOND[aiDevice]))
    : 0;

  const fileInput = (
    <input
      ref={inputRef}
      accept="image/jpeg,image/png,image/webp,image/gif,image/bmp,image/avif"
      className="tool-live-file-input"
      onChange={(event) => {
        void handleFile(event.target.files?.[0]);
        event.target.value = '';
      }}
      type="file"
    />
  );

  return (
    <article
      className={`tool-live-card sharpen-studio ${loaded ? 'sharpen-studio-loaded' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragActive(false);
        void handleFile(event.dataTransfer.files?.[0]);
      }}
    >
      <div className="tool-live-header">
        <div>
          <h2>
            {config?.heading ??
              (mode === 'unblur'
                ? 'Unblur an image'
                : mode === 'upscale'
                  ? 'Upscale an image with AI'
                  : 'Sharpen an image')}
          </h2>
          <p>
            {mode === 'unblur'
              ? 'Reverse focus and camera blur with deconvolution, at full resolution, in your browser. Nothing is uploaded.'
              : mode === 'upscale'
                ? 'Enlarge a small or low-resolution image 2x or 4x with a neural network that rebuilds detail and removes JPEG blocks. Runs on your device; nothing is uploaded.'
                : 'Crisp up edges and fine detail with an adjustable unsharp mask, at full resolution, in your browser. Nothing is uploaded.'}
          </p>
        </div>
        <span className="tool-live-badge">
          {mode === 'unblur' ? 'Deblur' : mode === 'upscale' ? 'AI upscale' : 'Unsharp mask'}
        </span>
      </div>
      {fileInput}

      {loaded ? null : (
        <button
          className={`sharpen-drop ${dragActive ? 'sharpen-drop-active' : ''}`}
          onClick={() => inputRef.current?.click()}
          type="button"
        >
          <svg aria-hidden="true" className="sharpen-drop-icon" viewBox="0 0 24 24">
            <path d="M4 16l4.5-4.5 3 3L16 10l4 4" />
            <rect height="16" rx="2.5" width="18" x="3" y="4" />
            <circle cx="8.5" cy="8.5" r="1.5" />
          </svg>
          <strong>Choose a photo</strong>
          <span>or drop it here. JPG, PNG, WebP, GIF, BMP or AVIF, up to 24 megapixels.</span>
        </button>
      )}

      <div className="sharpen-layout" hidden={!loaded}>
        <div className="sharpen-stage">
          <div className="sharpen-toolbar">
            <div className="sharpen-segmented" role="group" aria-label="Zoom">
              {(['fit', 1, 2] as Zoom[]).map((value) => (
                <button
                  aria-pressed={zoom === value}
                  key={String(value)}
                  onClick={() => setZoom(value)}
                  type="button"
                >
                  {value === 'fit' ? 'Fit' : `${value * 100}%`}
                </button>
              ))}
            </div>
            <span className="sharpen-hint">
              {zoom === 'fit'
                ? 'Click the photo to zoom to 100%. Drag the handle to compare.'
                : 'Drag to move around. Drag the handle to compare.'}
            </span>
          </div>
          <div
            className={`sharpen-view ${zoom === 'fit' ? 'sharpen-view-fit' : 'sharpen-view-zoom'}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={() => (dragRef.current = null)}
            ref={viewRef}
            style={{ height: viewHeight || undefined }}
          >
            <canvas className="sharpen-layer" ref={beforeRef} style={layerStyle} />
            <div className="sharpen-after" style={{ clipPath: `inset(0 0 0 ${split}%)` }}>
              <canvas className="sharpen-layer" ref={afterRef} style={layerStyle} />
            </div>
            <div className="sharpen-divider" style={{ left: `${split}%` }}>
              <span className="sharpen-knob" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M9 7l-5 5 5 5M15 7l5 5-5 5" />
                </svg>
              </span>
            </div>
            <span className="sharpen-tag sharpen-tag-left">Before</span>
            <span className="sharpen-tag sharpen-tag-right">After</span>
            {busy || aiRunning ? (
              <div className="sharpen-busy" role="status">
                <span
                  className="sharpen-busy-bar"
                  style={{ width: `${Math.round((aiRunning ? ai.fraction : progress) * 100)}%` }}
                />
                <span>
                  {aiRunning
                    ? ai.phase === 'download'
                      ? 'Loading AI model'
                      : 'AI enhancing'
                    : mode === 'unblur'
                      ? 'Unblurring'
                      : 'Sharpening'}{' '}
                  {Math.round((aiRunning ? ai.fraction : progress) * 100)}%
                </span>
              </div>
            ) : null}
          </div>
          <input
            aria-label="Before and after divider"
            className="sharpen-split-input"
            max={100}
            min={0}
            onChange={(event) => setSplit(Number(event.target.value))}
            type="range"
            value={split}
          />
        </div>

        <aside className="sharpen-panel">
          {loaded ? (
            <div className="sharpen-file">
              <div>
                <strong title={loaded.file.name}>{loaded.file.name}</strong>
                <span>
                  {loaded.width} × {loaded.height} · {formatFileSize(loaded.file.size)}
                  {loaded.scaled ? ' · scaled to 24 MP' : ''}
                </span>
              </div>
              <button
                className="sharpen-link-button"
                onClick={() => inputRef.current?.click()}
                type="button"
              >
                Replace
              </button>
            </div>
          ) : null}

          {mode === 'unblur' ? (
            <div className="sharpen-group">
              <span className="sharpen-label">Method</span>
              <div
                className="sharpen-segmented sharpen-segmented-full"
                role="group"
                aria-label="Method"
              >
                <button
                  aria-pressed={method === 'deconv'}
                  onClick={() => chooseMethod('deconv')}
                  type="button"
                >
                  Deconvolution
                </button>
                <button
                  aria-pressed={method === 'ai'}
                  onClick={() => chooseMethod('ai')}
                  type="button"
                >
                  AI upscale <span className="sharpen-beta">small images</span>
                </button>
              </div>
            </div>
          ) : null}

          {useAi ? (
            <div className="sharpen-group">
              <p className="sharpen-hint">
                Swin2SR, an open-source neural network trained on real blurry, noisy and compressed
                photos, enlarges the image and rebuilds detail on your device. It works on images up
                to {aiCap} px on this device
                {aiDevice === 'webgpu' ? '' : ' (no graphics acceleration)'}. The first run
                downloads a ~50 MB model; nothing is uploaded. It can invent texture, so compare at
                100%.
              </p>
              <div
                className="sharpen-segmented sharpen-segmented-full"
                role="group"
                aria-label="Scale"
              >
                {([2, 4] as const).map((value) => (
                  <button
                    aria-pressed={aiScale === value}
                    disabled={aiRunning}
                    key={value}
                    onClick={() => setAiScale(value)}
                    type="button"
                  >
                    {value}× larger
                  </button>
                ))}
              </div>
              {loaded && aiTooBig ? (
                <p className="sharpen-result sharpen-result-low">
                  This image is {Math.max(loaded.width, loaded.height)} px, too large to enlarge
                  here, and it already has enough pixels.{' '}
                  {mode === 'unblur'
                    ? 'Use Deconvolution above to make it crisper.'
                    : 'To make it crisper, use Unblur Image or Sharpen Image.'}
                </p>
              ) : loaded && ai.phase === 'idle' ? (
                <p className="sharpen-hint">
                  Output {loaded.width * aiScale} × {loaded.height * aiScale} px, about{' '}
                  {aiSeconds < 90
                    ? `${aiSeconds} seconds`
                    : `${Math.round(aiSeconds / 60)} minutes`}
                  .
                </p>
              ) : null}
              <button
                className="sharpen-secondary"
                disabled={aiRunning || aiTooBig}
                onClick={() => void enhanceWithAi()}
                type="button"
              >
                {aiRunning
                  ? ai.phase === 'download'
                    ? `Loading model ${Math.round(ai.fraction * 100)}%`
                    : `Enhancing ${Math.round(ai.fraction * 100)}%`
                  : ai.phase === 'done'
                    ? 'Run AI again'
                    : `Upscale ${aiScale}× with AI`}
              </button>
              {ai.phase === 'done' ? (
                <Slider
                  hint="Blend the AI result with your original."
                  label="AI strength"
                  max={100}
                  min={0}
                  onChange={(value) => {
                    setAiStrength(value / 100);
                    showAi(value / 100);
                  }}
                  step={1}
                  unit="%"
                  value={Math.round(aiStrength * 100)}
                />
              ) : null}
            </div>
          ) : (
            <>
              <div className="sharpen-group">
                <span className="sharpen-label">Preset</span>
                <div className="sharpen-chips" role="group" aria-label="Presets">
                  {presets.map((preset) => (
                    <button
                      aria-pressed={presetId === preset.id}
                      className="sharpen-chip"
                      key={preset.id}
                      onClick={() => applyPreset(preset.id)}
                      type="button"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sharpen-group">
                {mode === 'unblur' ? (
                  <>
                    <Slider
                      hint="How far edges are smeared. Too high adds halos."
                      label="Blur size"
                      max={6}
                      min={0.5}
                      onChange={(blur) => {
                        setPresetId('custom');
                        setUnblur((s) => ({ ...s, blur }));
                      }}
                      step={0.1}
                      unit=" px"
                      value={unblur.blur}
                    />
                    <Slider
                      hint="More passes, more detail and more noise."
                      label="Passes"
                      max={40}
                      min={3}
                      onChange={(iterations) => {
                        setPresetId('custom');
                        setUnblur((s) => ({ ...s, iterations }));
                      }}
                      step={1}
                      value={unblur.iterations}
                    />
                    <Slider
                      hint="Keeps skies, skin and walls smooth."
                      label="Noise suppression"
                      max={20}
                      min={0}
                      onChange={(noise) => {
                        setPresetId('custom');
                        setUnblur((s) => ({ ...s, noise }));
                      }}
                      step={1}
                      value={unblur.noise}
                    />
                  </>
                ) : (
                  <>
                    <Slider
                      hint="How strongly edges are boosted."
                      label="Amount"
                      max={4}
                      min={0}
                      onChange={(amount) => {
                        setPresetId('custom');
                        setSharpen((s) => ({ ...s, amount }));
                      }}
                      step={0.05}
                      value={sharpen.amount}
                    />
                    <Slider
                      hint="Small for text and texture, larger for soft photos."
                      label="Radius"
                      max={8}
                      min={0.3}
                      onChange={(radius) => {
                        setPresetId('custom');
                        setSharpen((s) => ({ ...s, radius }));
                      }}
                      step={0.1}
                      unit=" px"
                      value={sharpen.radius}
                    />
                    <Slider
                      hint="Skips tiny differences so noise stays put."
                      label="Threshold"
                      max={30}
                      min={0}
                      onChange={(threshold) => {
                        setPresetId('custom');
                        setSharpen((s) => ({ ...s, threshold }));
                      }}
                      step={1}
                      value={sharpen.threshold}
                    />
                  </>
                )}
              </div>
            </>
          )}

          {gain !== null && ready ? (
            <div className={`sharpen-result ${gain < 10 && !view ? 'sharpen-result-low' : ''}`}>
              <strong>{gain >= 0 ? `+${gain}%` : `${gain}%`}</strong>
              <span>
                {view
                  ? `fine detail versus a plain ${aiScale}× resize, now ${view.width} × ${view.height}`
                  : gain < 10
                    ? mode === 'unblur'
                      ? 'fine detail. Little changed: raise Blur size or Passes.'
                      : 'fine detail. Little changed: raise Amount or Radius.'
                    : `fine detail, same ${loaded?.width} × ${loaded?.height} size`}
              </span>
            </div>
          ) : null}

          <div className="sharpen-export">
            <div className="sharpen-segmented" role="group" aria-label="Format">
              {(Object.keys(FORMATS) as OutputFormat[]).map((key) => (
                <button
                  aria-pressed={format === key}
                  key={key}
                  onClick={() => setFormat(key)}
                  type="button"
                >
                  {FORMATS[key].label}
                </button>
              ))}
            </div>
            <button
              className="sharpen-download"
              disabled={!ready || busy || saving || aiRunning}
              onClick={() => void download()}
              type="button"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
              </svg>
              {busy || aiRunning
                ? 'Processing…'
                : saving
                  ? 'Saving…'
                  : `Download ${FORMATS[format].label}`}
            </button>
          </div>
          {error ? <p className="tool-error">{error}</p> : null}
        </aside>
      </div>

      {!loaded && error ? <p className="tool-error">{error}</p> : null}

      <p className="sharpen-hint sharpen-footnote">
        {mode === 'unblur'
          ? 'Works best on mild focus or camera-shake blur; no tool can rebuild a face a few pixels wide. '
          : mode === 'upscale'
            ? 'Photo already large but blurry? '
            : 'Photo out of focus rather than just soft? '}
        <Link href={otherTool.href}>
          {mode === 'unblur' ? 'For a lighter touch use' : 'Try'} {otherTool.label}
        </Link>
        .
      </p>
    </article>
  );
}

function Slider({
  label,
  hint,
  value,
  min,
  max,
  step,
  unit = '',
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="sharpen-slider">
      <span className="sharpen-slider-head">
        <span>{label}</span>
        <output>
          {step < 1 ? value.toFixed(step < 0.1 ? 2 : 1) : value}
          {unit}
        </output>
      </span>
      <input
        max={max}
        min={min}
        onChange={(event) => onChange(Number(event.target.value))}
        step={step}
        type="range"
        value={value}
      />
      <small>{hint}</small>
    </label>
  );
}
